import { useEffect, useState } from "react";
import Parse from "parse";
import { useDispatch } from "react-redux";
import {
  addToCart,
  addToMiscCartItems,
  patchQuotationNumber,
  setRevisionQuotation,
  setSelectedClient,
  setSelectedPercentageToWin,
  setSelectedQuotationType,
} from "../features/cart/cartSlice";
import {
  Box,
  Button,
  InputBase,
  Typography,
  useTheme,
} from "@mui/material";
import { Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import { tokens } from "../theme";
import { setRevisionInProgress } from "../features/cart/cartSlice";

/**
 * Wrapper class to simulate Parse.Object behavior in Redux.
 */
export class ParseObject {
  constructor(data, className, id) {
    this.data = data;
    this.className = className;
    this.id = id;
  }

  get(key) {
    return this.data?.[key];
  }
}

const ExistingQuotation = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // Local state to manage input and loading.
  const [quotationNumber, setQuotationNumber] = useState("");
  const [quotationNumberFound, setQuotationNumberFound] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Redux dispatch and navigation hooks.
  const dispatch = useDispatch();
  const navigate = useNavigate();

  /**
   * Check if the input format indicates a revision quotation.
   */
  const isRevision = (number) => /^[0-9]+-[A-Za-z]$/.test(number.trim());

  /**
   * Create cart item for Redux using ParseObject structure based on input type.
   */
  const buildCartItem = (item) => {
    const className = item.part?.data?.partID?.includes("canti")
      ? "CantileverParts"
      : "PalletRackingParts";

    const parseClone = new ParseObject(item.part.data, className, item.id);

    return {
      id: item.id,
      part: parseClone,
      quantity: item.quantity,
      price: parseInt(item.price),
      section: item.section,
    };
  };

  /**
   * Dispatch selected client to Redux store, creating required fields.
   */
  const dispatchClient = (clientData) => {
    if (!clientData) return;

    const clonedClient = {
      ...clientData,
      parseObject: clientData,
      email: clientData.email,
      label: `${clientData.companyName} - ${clientData.firstName} ${clientData.lastName} - ${clientData.email} - ${clientData.phoneNumber}`,
    };

    dispatch(setSelectedClient(clonedClient));
  };

  /**
   * Dispatch miscellaneous parts to Redux store.
   */
  const dispatchMiscParts = (miscParts) => {
    if (!miscParts?.length) return;

    miscParts.forEach((item) => dispatch(addToMiscCartItems(item)));
  };

  /**
   * When quotation fetch is successful, populate Redux and navigate to cart.
   */
  const handleSuccessfulFetch = (quoteObject, isRev = false) => {
    const cartItems = isRev
      ? quoteObject.get("quotationParts")
      : JSON.parse(quoteObject.get("cartItemsState")[0] || "[]");

    // Add each cart item to Redux store
    cartItems.forEach((item) => {
      const newItem = isRev
        ? {
            id: item.id,
            part: new ParseObject(
              item,
              item.partID?.includes("canti") ? "CantileverParts" : "PalletRackingParts",
              item.id
            ),
            quantity: item.quantity,
            price: parseInt(item.price),
            section: item.section,
          }
        : buildCartItem(item);

      dispatch(addToCart(newItem));
    });

    setQuotationNumberFound(quoteObject.get("quotationNumber"));

    // Dispatch client, misc parts, probability, and type to Redux
    dispatchClient(quoteObject.get("selectedClient")?.data);
    dispatchMiscParts(quoteObject.get("miscParts"));

    const winProb = quoteObject.get("winProbability");
    if (winProb) dispatch(setSelectedPercentageToWin(winProb));

    const quoteType = quoteObject.get("quotationType");
    dispatch(setSelectedQuotationType(quoteType?.length ? quoteType : []));

    // Redirect to cart page
    navigate("/cart");
  };

  /**
   * Fetch logic for revised quotation (with format like "123-A").
   */
  const fetchRevisionQuotation = async () => {
    const query = new Parse.Query("QuotationRevisions")
      .include("client")
      .equalTo("quotationNumber", quotationNumber)
      .equalTo("status", "in-process");

    try {
      const revisions = await query.find();

      if (revisions.length > 0) {
        dispatch(setRevisionInProgress(true));
        dispatch(setRevisionQuotation(revisions[0]));
        handleSuccessfulFetch(revisions[0], true);
      } else {
        toast.warning(`No in-process revision found for "${quotationNumber}"`);
      }
    } catch (error) {
      console.error("Revision fetch error:", error);
      toast.error("Failed to fetch revised quotation.");
    }
  };

  /**
   * Fetch logic for standard quotation (no revision suffix).
   */
  const fetchStandardQuotation = async () => {
    const query = new Parse.Query("Quotations")
      .include("client")
      .equalTo("quotationNumber", quotationNumber)
      .equalTo("quotationStatus", "in-process");

    try {
      const result = await query.find();

      if (result.length > 0) {
        dispatch(setRevisionInProgress(false));
        handleSuccessfulFetch(result[0]);
      } else {
        toast.warning(`No in-process quotation found for "${quotationNumber}"`);
      }
    } catch (error) {
      console.error("Standard fetch error:", error);
      toast.error("Failed to fetch quotation.");
    }
  };

  /**
   * Main handler to validate input and trigger fetch logic.
   */
  const getQuotation = async () => {
    if (!quotationNumber.trim()) {
      toast.warning("Please enter a quotation number.");
      return;
    }

    setIsLoading(true);

    // Determine if it's a revision or standard quotation
    if (isRevision(quotationNumber)) {
      await fetchRevisionQuotation();
    } else {
      await fetchStandardQuotation();
    }

    setIsLoading(false);
  };

  /**
   * Update Redux with matched quotation number after successful fetch.
   */
  useEffect(() => {
    if (quotationNumberFound.length > 0) {
      dispatch(patchQuotationNumber(quotationNumberFound));
    }
  }, [quotationNumberFound, dispatch]);

  return (
    <>
      <ToastContainer />
      {/* Input Row */}
      <Box display={"flex"} alignItems={"center"} my={1}>
        <Typography
          variant="h6"
          fontWeight="200"
          mx="2em"
          color={colors.greenAccent[400]}
        >
          Quotation Number
        </Typography>
        <InputBase
          sx={{ width: 300 }}
          placeholder="Enter quotation number"
          className="outlined border rounded p-1 mx-1"
          name="quoteNumber"
          value={quotationNumber}
          disabled={isLoading}
          onChange={(e) => setQuotationNumber(e.target.value)}
        />
      </Box>

      {/* Action or Loader */}
      {isLoading ? (
        <Box
          display={"flex"}
          flexDirection={"column"}
          justifyContent={"center"}
          alignItems={"center"}
          mt={4}
        >
          <Typography fontWeight="200" color={colors.greenAccent[400]}>
            Looking for quotation <b>{quotationNumber}</b>...
          </Typography>
          <Spinner animation="border" role="status" size="sm" variant="primary" />
        </Box>
      ) : (
        <Box display={"flex"} justifyContent={"center"} mt={2}>
          <Button
            sx={{
              backgroundColor: colors.blueAccent[700],
              color: colors.grey[100],
              fontSize: "14px",
              fontWeight: "bold",
              padding: "10px 20px",
            }}
            onClick={getQuotation}
            disabled={isLoading}
          >
            Look for Existing Quotation
          </Button>
        </Box>
      )}
    </>
  );
};

export default ExistingQuotation;